import { merge } from 'lodash-es';

import type { VariantProp } from '../../common/colors';
import type { GutterProp } from '../../utilities/shared/Gutter';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { gutterCSS } from '../../utilities/shared/Gutter';
import { type WidthTypes, widthCSS } from '../../utilities/shared/Width';

const DividerFill = styled('div', {
  display: 'flex',
  borderRadius: '99em',
  transitionProperty: 'background-color',
  transitionDuration: '50ms',
  transitionTimingFunction: 'ease-out',

  variants: {
    appearance: {
      thick: {},
      thin: {},
      none: {},
    },
    direction: {
      column: {
        heightAll: '100%',
      },
      row: {
        widthAll: '100%',
      },
    },
    variant: {
      alternative: {
        backgroundColor: colors.strokeAlternativeLight,

        [darkThemeSelector]: {
          backgroundColor: colors.strokeAlternativeDark,
        },
      },
      attention: {
        backgroundColor: colors.strokeAttentionLight,

        [darkThemeSelector]: {
          backgroundColor: colors.strokeAttentionDark,
        },
      },
      brand: {
        backgroundColor: colors.strokeBrandLight,

        [darkThemeSelector]: {
          backgroundColor: colors.strokeBrandDark,
        },
      },
      negative: {
        backgroundColor: colors.strokeNegativeLight,

        [darkThemeSelector]: {
          backgroundColor: colors.strokeNegativeDark,
        },
      },
      neutral: {
        backgroundColor: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          backgroundColor: colors.strokeNeutralDark,
        },
      },
      positive: {
        backgroundColor: colors.strokePositiveLight,

        [darkThemeSelector]: {
          backgroundColor: colors.strokePositiveDark,
        },
      },
      selected: {
        backgroundColor: colors.iconBrandLight,

        [darkThemeSelector]: {
          backgroundColor: colors.iconBrandDark,
        },
      },
    },
  },

  compoundVariants: [
    {
      appearance: 'thick',
      direction: 'column',
      css: { widthAll: '$2' },
    },
    {
      appearance: 'thick',
      direction: 'row',
      css: { heightAll: '$2' },
    },
    {
      appearance: 'thin',
      direction: 'column',
      css: { widthAll: '$1' },
    },
    {
      appearance: 'thin',
      direction: 'row',
      css: { heightAll: '$1' },
    },
  ],
});
DividerFill.displayName = 'DividerFill';

const DividerContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  variants: {
    direction: {
      column: {
        alignSelf: 'stretch',
        widthAll: 0,
      },
      row: {
        heightAll: 0,
      },
    },
  },
});
DividerContainer.displayName = 'DividerContainer';

export type DividerAppearance = 'thick' | 'thin' | 'none';

export type DividerProps = {
  appearance?: DividerAppearance;
  direction?: 'column' | 'row';
  variant?: 'selected' | VariantProp;
  width?: WidthTypes;
} & GutterProp;

export function Divider({
  appearance = 'thin',
  direction = 'row',
  gutter,
  variant = 'neutral',
  width,
  ...remaining
}: DividerProps) {
  const css = merge(gutter && gutterCSS(gutter), width && widthCSS(width));

  return (
    <DividerContainer direction={direction} css={css} {...remaining}>
      <DividerFill appearance={appearance} direction={direction} variant={variant} />
    </DividerContainer>
  );
}
